import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { ClientAccountPaymentVerification, PaymentMethodLockPeriod, PaymentMethodType, AdminNote } from '../types/ClientAccountPayment';

interface ClientAccountPaymentState {
    verification: ClientAccountPaymentVerification | null;
    isLoading: boolean;
    error: string | null;
    currentPaymentMethod: PaymentMethodType | null;
    lockPeriod: PaymentMethodLockPeriod | null;
    formData: {
        financialInstitution: string;
        accountHolderName: string;
        businessName: string;
        institutionName: string;
        institutionNumber: string;
        branchNumber: string;
        accountNumber: string;
        paymentInstructions: string;
        proofOfOwnershipFile: File | null;
        declarationAccepted: boolean;
    };
    currentStep: number;
}

const initialState: ClientAccountPaymentState = {
    verification: null,
    isLoading: false,
    error: null,
    currentPaymentMethod: null,
    lockPeriod: null,
    formData: {
        financialInstitution: '',
        accountHolderName: '',
        businessName: '',
        institutionName: '',
        institutionNumber: '',
        branchNumber: '',
        accountNumber: '',
        paymentInstructions: '',
        proofOfOwnershipFile: null,
        declarationAccepted: false,
    },
    currentStep: 0,
};

const clientAccountPaymentSlice = createSlice({
    name: 'clientAccountPayment',
    initialState,
    reducers: {
        // Verification actions
        setVerification: (state, action: PayloadAction<ClientAccountPaymentVerification | null>) => {
            state.verification = action.payload;
        },
        setIsLoading: (state, action: PayloadAction<boolean>) => {
            state.isLoading = action.payload;
        },
        setError: (state, action: PayloadAction<string | null>) => {
            state.error = action.payload;
        },
        setCurrentPaymentMethod: (state, action: PayloadAction<PaymentMethodType | null>) => {
            state.currentPaymentMethod = action.payload;
        },
        setLockPeriod: (state, action: PayloadAction<PaymentMethodLockPeriod | null>) => {
            state.lockPeriod = action.payload;
        },
        
        // Form data actions
        updateFormData: (state, action: PayloadAction<Partial<typeof state.formData>>) => {
            state.formData = { ...state.formData, ...action.payload };
        },
        resetFormData: (state) => {
            state.formData = initialState.formData;
        },
        setProofOfOwnershipFile: (state, action: PayloadAction<File | null>) => {
            state.formData.proofOfOwnershipFile = action.payload;
        },
        
        // Step management
        setCurrentStep: (state, action: PayloadAction<number>) => {
            state.currentStep = action.payload;
        },
        nextStep: (state) => {
            state.currentStep += 1;
        },
        previousStep: (state) => {
            if (state.currentStep > 0) {
                state.currentStep -= 1;
            }
        },
        
        // Notes management
        addNote: (state, action: PayloadAction<AdminNote>) => {
            if (state.verification) {
                if (!state.verification.notes) {
                    state.verification.notes = [];
                }
                state.verification.notes.push(action.payload);
            }
        },
        
        // Clear state
        clearClientAccountPaymentState: (state) => {
            state.verification = null;
            state.isLoading = false;
            state.error = null;
            state.currentPaymentMethod = null;
            state.lockPeriod = null;
            state.formData = initialState.formData;
            state.currentStep = 0;
        },
    },
});

export const {
    setVerification,
    setIsLoading,
    setError,
    setCurrentPaymentMethod,
    setLockPeriod,
    updateFormData,
    resetFormData,
    setProofOfOwnershipFile,
    setCurrentStep,
    nextStep,
    previousStep,
    addNote,
    clearClientAccountPaymentState,
} = clientAccountPaymentSlice.actions;

export default clientAccountPaymentSlice.reducer;
